// src/scripts/useScripts/usePettyCashApi.js
import { computed, ref, watch } from "vue";
import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

import { pettyCashVsRevenueResolve } from "@/scripts/sqlApiHelper/pettyCashApi";

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function parseISODate(iso) {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toISODateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function computeWeekFromTo(weekEndingIso, tradingDateIso) {
  const we = parseISODate(weekEndingIso);
  const td = parseISODate(tradingDateIso);
  const end = we || td;

  if (!end) return { from: "", to: "" };

  const to = toISODateLocal(end);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);

  return {
    from: toISODateLocal(start),
    to,
  };
}

export function usePettyCashApi() {
  const { venueId, tradingDate, weekEnding } = useVenueContext();

  const loading = ref(false);
  const error = ref("");

  const pettyCashTotal = ref(0);
  const actualRevenueTotal = ref(0);

  const weekRange = computed(() =>
    computeWeekFromTo(weekEnding.value, tradingDate.value)
  );

  const pctOfRevenue = computed(() => {
    const rev = toNumber(actualRevenueTotal.value);
    if (rev <= 0) return 0;
    return (toNumber(pettyCashTotal.value) / rev) * 100;
  });

  async function load() {
    error.value = "";
    pettyCashTotal.value = 0;
    actualRevenueTotal.value = 0;

    const venue_id = String(venueId.value || "");
    const week_ending_date = String(weekEnding.value || weekRange.value.to || "");

    if (!venue_id || !week_ending_date) return;

    loading.value = true;

    try {
      const res = await pettyCashVsRevenueResolve({
        venue_id,
        week_ending_date,
      });

      pettyCashTotal.value = toNumber(res?.petty_cash_total ?? 0);
      actualRevenueTotal.value = toNumber(res?.actual_revenue_total ?? 0);
    } catch (e) {
      error.value = e?.message || String(e);
      pettyCashTotal.value = 0;
      actualRevenueTotal.value = 0;
    } finally {
      loading.value = false;
    }
  }

  watch([venueId, weekEnding, tradingDate], load, { immediate: true });

  return {
    loading,
    error,
    weekRange,
    pettyCashTotal,
    actualRevenueTotal,
    pctOfRevenue,
    reload: load,
  };
}

export default usePettyCashApi;